import { faPause } from '@fortawesome/free-solid-svg-icons';
import { faCreditCard, faBan, faPlay, faMoneyBill } from '@fortawesome/pro-regular-svg-icons';
import { faArrowRotateRight } from '@fortawesome/pro-solid-svg-icons';
import { Enrollment, MembershipItem } from '@spike/membership-model';
import { Permissions } from 'hooks/usePermissions';
import moment, { Moment } from 'moment-timezone';

export type EnrollmentAction = 'update_card' | 'cancel' | 'collect_payment' | 'pause' | 'resume' | 'try_again';

export interface EnrollmentActionConfig {
    label: string;
    icon: any;
    action: EnrollmentAction;
}

export function getEnrollmentActions(
    status: string,
    enrollmentType: string,
    permissions: Permissions
): EnrollmentActionConfig[] {
    let actions: EnrollmentActionConfig[] = [];

    switch (status) {
        case 'active':
            actions = [
                { label: 'Update Card', icon: faCreditCard, action: 'update_card' },
                { label: 'Pause', icon: faPause, action: 'pause' },
                { label: 'Cancel', icon: faBan, action: 'cancel' }
            ];
            break;
        case 'pending':
            actions = [
                { label: 'Collect Payment', icon: faMoneyBill, action: 'collect_payment' },
                { label: 'Cancel', icon: faBan, action: 'cancel' }
            ];
            break;
        case 'paused':
            actions = [
                { label: 'Resume', icon: faPlay, action: 'resume' },
                { label: 'Cancel', icon: faBan, action: 'cancel' }
            ];
            break;
        case 'past_due':
            actions = [
                { label: 'Try Again', icon: faArrowRotateRight, action: 'try_again' },
                { label: 'Update Card', icon: faCreditCard, action: 'update_card' },
                { label: 'Cancel', icon: faBan, action: 'cancel' }
            ];
            break;
        default:
            actions = [];
    }

    // Filter out 'Update Card' action if enrollmentType is 'package'
    if (enrollmentType === 'package') {
        actions = actions.filter(action => action.action !== 'update_card');
    } else {
        actions = actions.filter(action => !['pause', 'resume'].includes(action.action));
    }

    // Filter out the action if not allowed
    actions = actions
        .filter(action => action.action !== 'cancel' || permissions.memberships.cancel)
        .filter(action => action.action !== 'pause' || permissions.memberships.pauseResume)
        .filter(action => action.action !== 'resume' || permissions.memberships.pauseResume)
        .filter(action => action.action !== 'collect_payment' || permissions.memberships.managePayments)
        .filter(action => action.action !== 'try_again' || permissions.memberships.managePayments)
        .filter(action => action.action !== 'update_card' || permissions.memberships.managePayments);

    return actions;
}

export function getEnrollmentStatusClass(status: string): string {
    switch (status.toLowerCase()) {
        case 'active':
            return 'badgeActive';
        case 'pending':
            return 'badgePending';
        case 'canceled':
            return 'badgeCanceled';
        case 'paused':
            return 'badgePaused';
        case 'past_due':
            return 'badgePastDue';
        default:
            return 'badgeActive';
    }
}

export function getEnrollmentStartDate(enrollment: Enrollment): Moment {
    return enrollment.startAt || enrollment.startDate;
}

export function getEnrollmentEndDate(enrollment: Enrollment): Moment | string {
    return enrollment.endAt || enrollment.endDate;
}

export function getEnrollmentEndAtPatchValue(enrollment: Enrollment, timezone: string): string | null {
    const endDate = getEnrollmentEndDate(enrollment);

    if (!endDate || !moment(endDate).isValid()) {
        return null;
    }

    return moment(endDate).tz(timezone).endOf('day').toISOString();
}

export function getEnrollmentResumeEndAtPatchValue(enrollment: Enrollment, timezone: string): string | null {
    const endDate = getEnrollmentEndDate(enrollment);

    if (!enrollment.pausedAt || !endDate || !moment(endDate).isValid()) {
        return getEnrollmentEndAtPatchValue(enrollment, timezone);
    }

    const pausedAt = moment(enrollment.pausedAt).tz(timezone).startOf('day');
    const originalEndDate = moment(endDate).tz(timezone).startOf('day');
    const daysRemaining = Math.max(originalEndDate.diff(pausedAt, 'days'), 0);

    return moment().tz(timezone).startOf('day').add(daysRemaining, 'days').utc().endOf('day').toISOString();
}

/*
Legacy function to calculate usage for a membership item
export function calculateUsage(item: MembershipItem): { used: number; total: string | number } {
    const used = item.unlimited ? 0 : Math.max(0, item.quantity - (item.left ?? 0));
    const total = item.unlimited ? 'Unlimited' : item.quantity;
    return { used, total };
}
*/

export function calculateUsage(item: MembershipItem): { used: number | string; total: string | number } {
    // If unlimited, return directly
    if (item.unlimited) {
        const used = item.consumed ?? 0;
        return { used, total: 'Unlimited' };
    }

    // For limited items, use consumed from backend if available
    if (item.consumed !== undefined && item.consumed !== null) {
        const consumed = item.consumed;
        const left = typeof item.left === 'number' ? item.left : 0;
        const total = consumed + left; // ← Total real con rollover
        return { used: consumed, total };
    }

    // Fallback: frontend calculation (backward compatibility)
    const used = Math.max(0, item.quantity - (item.left ?? 0));
    const total = item.quantity;
    return { used, total };
}
